import React, { useCallback, useMemo, useState, useReducer } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppMain } from '../layout/appMain';
import { useUserRoles } from '../hooks/useUserRoles';
import { Theme, useMediaQuery } from '@mui/material';
import { FieldValues } from 'react-hook-form';
import { SearchQueryParams, ViewMode } from '../models/reports';
import { getUnixTime, format } from 'date-fns';
import {
  filtersReducer,
  ReportsFiltersProvider,
  ReportsViewModeProvider,
} from '../context/reports/provider';
import { EmployeeAllocationsModal } from '../components/reports/employeeAllocationsModal';
import { ProjectAllocationsModal } from '../components/reports/projectAllocationsModal';
import { ReportsPageSmallLayout } from '../components/reports/reportsPageSmallLayout';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useAppDispatch } from '../hooks';
import { useSelector } from 'react-redux';
import {
  selectIsDialogVisible,
  setIsExportDialogVisible,
} from '../store/reports/reportsSlice';
import { ReportsPageLargeLayout } from '../components/reports/reportsPageLargeLayout';
import { ActivitiesContextProvider } from '../context/activities';
import { isValidViewModeForRoleId } from '../services/reportsService';

export function PageReports(): JSX.Element {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );
  const [filters, dispatchFilters] = useReducer(filtersReducer, null);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParams = useMemo(
    () => Object.fromEntries(new URLSearchParams(searchParams)),
    [searchParams]
  );

  const userRoles = useUserRoles();

  const isExportDialogVisible = useSelector(selectIsDialogVisible);
  const dispatch = useAppDispatch();

  const defaultViewMode = useMemo(() => {
    const { isAdmin, isTeamLead, isProjectManager, isBudgetOwner } = userRoles;

    const queryViewMode = queryParams.viewMode as ViewMode;
    if (Object.values(ViewMode).includes(queryViewMode)) {
      if (isValidViewModeForRoleId(queryViewMode, userRoles)) {
        return queryViewMode;
      }
    }
    if (isProjectManager || isBudgetOwner || isAdmin) {
      return ViewMode.PROJECT;
    }
    if (isAdmin || isTeamLead) {
      return ViewMode.PEOPLE;
    }

    return ViewMode.PEOPLE;
  }, [queryParams.viewMode, userRoles]);

  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);
  const [isOpenAllocationsModal, setIsOpenAllocationsModal] = useState(false);
  const [viewUnloggedProjects, setViewUnloggedProjects] = useState(false);

  const handleAllocationsModal = useCallback(() => {
    setIsOpenAllocationsModal(!isOpenAllocationsModal);
  }, [isOpenAllocationsModal]);

  const handleOpenExportDialog = useCallback(() => {
    dispatch(setIsExportDialogVisible(!isExportDialogVisible));
  }, [dispatch, isExportDialogVisible]);

  const handleDisplay = useCallback(
    (data: FieldValues) => {
      dispatchFilters({
        employeeId: data.engineer?.id || null,
        employeeName: data.engineer?.name || null,
        managerId: data.manager?.id || null,
        managerName: data.manager?.name || null,
        projectId: data.project?.id || null,
        projectName: data.project?.name || null,
        projectCode: data.project?.code || null,
        employeeLocation: data.engineer?.location || '',
        towerId: data.tower?.id || null,
        startDate: format(data.startDate, 'yyyy-MM-dd'),
        endDate: format(data.endDate, 'yyyy-MM-dd'),
        monthSelect: data.monthSelect,
        showHierarchy: filters?.showHierarchy || false,
        includeInactive: filters?.includeInactive || false,
      });

      const searchQueryParams: SearchQueryParams = {};
      if (data.engineer) {
        searchQueryParams.employeeId = data.engineer.id;
      }

      if (data.manager) {
        searchQueryParams.managerId = data.manager.id;
      }

      if (data.project) {
        searchQueryParams.projectId = data.project.id;
      }

      searchQueryParams.startDate = getUnixTime(data.startDate).toString();
      searchQueryParams.endDate = getUnixTime(data.endDate).toString();
      searchQueryParams.monthSelect = data.monthSelect;
      searchQueryParams.viewMode = viewMode;

      setSearchParams(searchQueryParams);
    },
    [filters, setSearchParams, viewMode]
  );

  const headerButtons = useMemo(() => {
    return [
      { icon: FileDownloadOutlinedIcon, onClick: handleOpenExportDialog },
    ];
  }, [handleOpenExportDialog]);

  const activitiesContext = useMemo(
    () => ({
      isActivitiesReadOnly: viewMode === ViewMode.PROJECT,
      showEmployeeName: viewMode === ViewMode.PROJECT,
    }),
    [viewMode]
  );

  const handleUnloggedProjectsCheck = useCallback((isUnloggedProjects) => {
    setViewUnloggedProjects(isUnloggedProjects);
  }, []);

  return (
    <AppMain buttons={headerButtons}>
      <ReportsViewModeProvider viewMode={viewMode} setViewMode={setViewMode}>
        <ReportsFiltersProvider filters={filters} dispatch={dispatchFilters}>
          <ActivitiesContextProvider value={activitiesContext}>
            {isMobile ? (
              <ReportsPageSmallLayout
                handleDisplay={handleDisplay}
                handleUnloggedProjectsCheck={handleUnloggedProjectsCheck}
              />
            ) : (
              <ReportsPageLargeLayout
                handleDisplay={handleDisplay}
                handleUnloggedProjectsCheck={handleUnloggedProjectsCheck}
              />
            )}

            {filters && (
              <>
                {viewMode === ViewMode.PEOPLE ? (
                  <EmployeeAllocationsModal
                    filters={filters}
                    handleClose={handleAllocationsModal}
                    isOpen={isOpenAllocationsModal}
                  />
                ) : (
                  <ProjectAllocationsModal
                    filters={filters}
                    viewUnloggedProjects={viewUnloggedProjects}
                    handleClose={handleAllocationsModal}
                    isOpen={isOpenAllocationsModal}
                  />
                )}
              </>
            )}
          </ActivitiesContextProvider>
        </ReportsFiltersProvider>
      </ReportsViewModeProvider>
    </AppMain>
  );
}
